import { get, range, set } from 'lodash';
import min from 'lodash/min';
import { camelCase, startCase } from 'lodash/string';

class EditLedgerCtrl {
  constructor(
    Ledger,
    $filter,
    $mdDialog,
    $mdToast,
    LedgerCategoryCollection,
    CurrencyCollection,
    TemplateLedgerCollection,
    User,
    Client,
    ClientSettings,
    FdsConnectionCollection,
  ) {
    this.Ledger = Ledger;
    this.$filter = $filter;
    this.$mdDialog = $mdDialog;
    this.$mdToast = $mdToast;
    this.LedgerCategoryCollection = LedgerCategoryCollection;
    this.TemplateLedgerCollection = TemplateLedgerCollection;
    this.CurrencyCollection = CurrencyCollection;
    this.Client = Client;
    this.ClientSettings = ClientSettings;
    this.clientId = User.getClientId();
    this.FdsConnectionCollection = FdsConnectionCollection;
    this.volumeKeys = this.Ledger.volumeKeys;
    this.volumeLabels = this.Ledger.volumeLabels;
    this.onDateActiveChange = this.onDateActiveChange.bind(this);
    this.onDateInactiveChange = this.onDateInactiveChange.bind(this);
    this.onIsFunctionalChange = this.onIsFunctionalChange.bind(this);
    this.setNewLedgerCurrency = this.setNewLedgerCurrency.bind(this);
  }

  $onInit() {
    this.ledger = new this.Ledger({
      id: this.ledgerId || null,
      clientId: this.clientId,
    });
    this.ledgerCategories = new this.LedgerCategoryCollection();
    this.templateLedgerCollection = new this.TemplateLedgerCollection();
    this.currencies = new this.CurrencyCollection();
    this.client = new this.Client(this.clientId);
    this.ledgerVM = this.ledger.$viewModel();
    this.client.$onSet(this.setNewLedgerCurrency);
    this.clientSettings = new this.ClientSettings(this.clientId);
    this.financialConnections = new this.FdsConnectionCollection(this.clientId);
    this.selectedFdsAccount = this.financialConnections.getAccountMappedToLedger(this.ledgerId);
    this.fdsAccounts = this.financialConnections.getAccounts();
    this.showDryRunMessage = false;

    if (!this.ledgerId || !this.ledgerVM.isFunctional) {
      this.setDefaults();
    }
  }

  setDefaults() {
    if (!this.ledgerVM.statementDayOfMonth) {
      set(this.ledgerVM, 'statementDayOfMonth', 1);
    }

    if (!get(this.ledgerVM, 'accountSettings.dateActive')) {
      set(this.ledgerVM, 'accountSettings.dateActive', this.getCurrentDate());
    }

    if (!get(this.ledgerVM, 'accountSettings.volume')) {
      set(this.ledgerVM, 'accountSettings.volume', 'STANDARD');
    }
  }

  setNewLedgerCurrency() {
    if (this.ledger.$isNew()) {
      this.ledgerVM.currencyId = this.client.currencyId;
    }
  }

  getCurrentDate() {
    return this.$filter('date')(new Date(), 'yyyy-MM');
  }

  getLedgerPurpose() {
    if (!this.ledgerId) {
      return 'Customized';
    }
    const templateLedger = this.templateLedgerCollection.findLedgerByCodeAndCountry(
      this.ledgerVM.code,
      this.client.countryCode,
    );

    return templateLedger ? templateLedger.name : 'Customized';
  }

  showFunctionalLedgerCheckbox() {
    return this.ledgerCategories.isPotentialFunctionalLedger(this.ledgerVM.categoryId);
  }

  isLedgerToggleDisabled() {
    // editable if it's a ledger
    if (this.ledgerVM.role) {
      return false;
    }

    return this.ledgerVM.accountType !== 'FLAVOUR';
  }

  cancel() {
    this.$mdDialog.cancel();
  }

  onDateActiveChange(dateActive) {
    this.ledgerVM.accountSettings.dateActive = dateActive;
  }

  onDateInactiveChange(dateInactive) {
    this.ledgerVM.accountSettings.dateInactive = dateInactive || null;
  }

  onIsFunctionalChange() {
    if (this.ledgerId) {
      return;
    }

    if (this.ledgerVM.isFunctional && ['6', '7', '8'].includes(this.ledgerVM.categoryId)) {
      this.ledgerVM.details = 'Use for automation with payroll or invoicing providers';
    } else {
      this.ledgerVM.details = '';
    }
  }

  invalidDates() {
    const active = new Date(this.ledgerVM.accountSettings.dateActive);
    const inactive = new Date(this.ledgerVM.accountSettings.dateInactive);

    return (
      this.ledgerVM.accountSettings.dateActive &&
      this.ledgerVM.accountSettings.dateInactive &&
      active > inactive
    );
  }

  /**
   * Saves the new ledger mapping based on the selected account for this ledger.
   * If the account is null when it previously had a value, the account will be unmapped.
   */
  async saveFdsLedgerMapping(dryRun = false) {
    const account = this.financialConnections.getAccountMappedToLedger(this.ledger.id);
    const oldAccountId = account ? account.id : null;
    const newAccountId = this.selectedFdsAccount ? this.selectedFdsAccount.id : null;

    if (oldAccountId === newAccountId) {
      return;
    }

    const accountId = newAccountId || oldAccountId; // fallback to old account id when unmapping a ledger
    const connection = this.financialConnections.getConnectionForAccount(accountId);

    return this.financialConnections.$updateLedgerMapping(
      connection.id,
      accountId,
      newAccountId ? this.ledger.id : null, // set a null ledger id when unmapping it from the account
      dryRun,
    );
  }

  onChangeFdsAccount() {
    if (!this.ledger.id) {
      return;
    }
    this.updateMinInvalidationDate();
  }

  updateMinInvalidationDate() {
    this.showDryRunMessage = false;
    this.saveFdsLedgerMapping(true).then(responseData => {
      if (!responseData) return;
      this.minInvalidationDate = min(
        responseData.data.updateAccountLedgerMapping.invalidatedBalances
          .filter(invalidationItem => invalidationItem.numBalancesInvalidated > 0)
          .map(invalidationItem => invalidationItem.newLatestValidBalanceDate),
      );

      this.showDryRunMessage = true;
    });
  }

  getConnectionsMappedToLedger(ledgerId) {
    return this.financialConnections.data.filter(connection =>
      connection.accounts.some(account => account.ledger && account.ledger.id === ledgerId),
    );
  }

  isMappingCrossSource() {
    if (!this.selectedFdsAccount) {
      return false;
    }

    const ledgerConnections = this.getConnectionsMappedToLedger(this.ledgerId);
    if (ledgerConnections.length === 0) {
      return false;
    }

    return this.selectedFdsAccount.institution.source !== ledgerConnections[0].source;
  }

  save() {
    this.ledgerVM.statementDayOfMonth = parseInt(this.ledgerVM.statementDayOfMonth, 10);
    const data = this.ledgerVM.$data();
    // wipe `accountSettings`, `statementDayOfMonth` if the ledger isn't an account
    if (!this.ledgerVM.isFunctional) {
      data.accountSettings = null;
      data.statementDayOfMonth = null;
    }

    this.ledger
      .$createOrUpdate(data)
      .then(updatedLedger => {
        this.ledger = '$storeId' in updatedLedger ? updatedLedger : this.ledger;
        return this.saveFdsLedgerMapping();
      })
      .then(() => this.$mdDialog.hide())
      .catch(e => {
        const message =
          get(e, 'data.messages[0].message') || 'There was a problem saving your changes';

        this.$mdToast.show(this.$mdToast.simple().textContent(message).hideDelay(3000));
      });
  }

  capitalizeText(text) {
    return startCase(camelCase(text));
  }

  hasRequiredError(key) {
    return !!get(this.ledgerVMForm, `${key}.$error.required`);
  }

  isFormInvalid() {
    if (this.ledgerVM.isFunctional && this.hasRequiredError('dateActive')) {
      return true;
    }

    if (this.ledgerVM.isFunctional && this.hasRequiredError('currencyId')) {
      return true;
    }

    if (!this.ledgerVM.name) {
      return true;
    }

    return !!this.invalidDates();
  }
}

EditLedgerCtrl.prototype.collectionSelection = [
  'PLAID',
  '3RD PARTY LOGIN',
  'OTHER INTEGRATION',
  'NOT REQUIRED',
  'CANNOT OPTIMIZE',
  'REQUESTED',
  'GUSTO',
  'SHOPIFY',
];
EditLedgerCtrl.prototype.optimizationSelection = [
  'Optimized',
  'Cannot Optimize',
  'Not Optimized',
  'Not Available',
  'Opt Out',
];
EditLedgerCtrl.prototype.statementDaysOfMonth = range(1, 32);

export default EditLedgerCtrl;
